/**
 * Asset leaderboard (Task 3) - NPC ranker definitions.
 * A purely LOCAL, server-free ranking: NPC net worth grows over real time so
 * the player can climb an actual ladder as their own total assets rise.
 * Names are meme handles (locale-independent). Growth is deterministic per NPC
 * so the same save always shows the same ladder (no random reshuffles).
 */

export interface NpcRanker {
  /** stable id (used as a seed + save key) */
  id: string;
  /** display handle - locale-independent meme nickname */
  name: string;
  /** starting net worth at t=0 (assetsAtStart in state) */
  baseAssets: number;
  /**
   * Compound growth per real hour (e.g. 0.04 = +4%/h). Whales grow slowly in %
   * but from a huge base; small fish grow fast in % from a tiny base. This makes
   * the mid ladder feel alive and passable while the top stays aspirational.
   */
  growthPerHour: number;
}

/**
 * The ladder. Sorted loosely high -> low by base, but growth rates cross over
 * time so ranks actually shuffle among NPCs (not just a static list).
 * Top "legend" tier is deliberately out of early reach (aspiration).
 */
export const NPC_RANKERS: readonly NpcRanker[] = [
  // === Legend tier (aspirational - massive base, slow % growth) ===
  { id: 'npc_whaleking', name: 'WhaleKing', baseAssets: 9.2e15, growthPerHour: 0.010 },
  { id: 'npc_jonberlegend', name: 'JonberLegend', baseAssets: 3.1e15, growthPerHour: 0.014 },
  { id: 'npc_satoshinephew', name: 'satoshi_nephew', baseAssets: 1.4e15, growthPerHour: 0.018 },
  // === Big fish ===
  { id: 'npc_diamondpaws', name: 'DiamondPaws', baseAssets: 4.2e14, growthPerHour: 0.024 },
  { id: 'npc_chartwizard', name: 'ChartWizard', baseAssets: 1.6e14, growthPerHour: 0.030 },
  { id: 'npc_moonhunter', name: 'moon_hunter88', baseAssets: 6.5e13, growthPerHour: 0.036 },
  { id: 'npc_insidermaybe', name: 'Insider_Maybe', baseAssets: 2.4e13, growthPerHour: 0.042 },
  // === Mid ladder (the passable pack) ===
  { id: 'npc_serpump', name: 'ser_pump', baseAssets: 9.0e12, growthPerHour: 0.05 },
  { id: 'npc_dipbuyer', name: 'dip_buyer_777', baseAssets: 3.2e12, growthPerHour: 0.058 },
  { id: 'npc_lambosoon', name: 'LamboSoon', baseAssets: 1.1e12, growthPerHour: 0.066 },
  { id: 'npc_captncandle', name: 'Cptn_Candle', baseAssets: 4.0e11, growthPerHour: 0.074 },
  { id: 'npc_hodlgrandma', name: 'fomo_grandma', baseAssets: 1.4e11, growthPerHour: 0.082 },
  { id: 'npc_gazua', name: 'chart_gazua', baseAssets: 5.0e10, growthPerHour: 0.09 },
  // === Small fish (fast % climbers from a small base) ===
  { id: 'npc_bagholder', name: 'bagholder_lee', baseAssets: 1.7e10, growthPerHour: 0.10 },
  { id: 'npc_rektagain', name: 'rekt_again', baseAssets: 6.0e9, growthPerHour: 0.11 },
  { id: 'npc_paperhands', name: 'PanicSeller', baseAssets: 2.0e9, growthPerHour: 0.12 },
  { id: 'npc_ramyeondiet', name: 'ramyeon_diet', baseAssets: 7.0e8, growthPerHour: 0.13 },
  { id: 'npc_seedmelting', name: 'seed_melting', baseAssets: 2.5e8, growthPerHour: 0.14 },
  { id: 'npc_apenobrain', name: 'ape_no_brain', baseAssets: 9.0e7, growthPerHour: 0.15 },
  { id: 'npc_exitliquidity', name: 'exit_liquidity', baseAssets: 3.0e7, growthPerHour: 0.16 },
] as const;

export const LEADERBOARD_CONFIG = {
  /** cap the compounding window so an idle NPC does not overflow to Infinity */
  maxGrowthHours: 24 * 30,
  /** hard ceiling on any single NPC's net worth (safety clamp) */
  maxAssets: 1e19,
} as const;

/**
 * NPC net worth at a given elapsed time (hours since the ladder epoch).
 * Pure + deterministic: assets = base * (1 + growthPerHour) ^ hours, clamped.
 */
export function npcAssetsAt(npc: NpcRanker, elapsedHours: number): number {
  const hours = Math.max(0, Math.min(elapsedHours, LEADERBOARD_CONFIG.maxGrowthHours));
  const assets = npc.baseAssets * Math.pow(1 + npc.growthPerHour, hours);
  return Math.min(assets, LEADERBOARD_CONFIG.maxAssets);
}
